import React, { useState, useEffect } from 'react'
import { FileText, ChevronDown } from 'lucide-react'
import { sileo } from 'sileo'
import { FacturaAPI } from '../../../../../../../../api/facturas.api'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import FacturaTotales from '../../createFactura/FacturaTotales'

export default function InformacionDocumento({
  formState,
  setFormState,
  errors,
  clearError,
  setMasterEntityId
}) {
  const [rutSearch, setRutSearch] = useState(formState.client?.rut || '')
  const [loadingClient, setLoadingClient] = useState(false)
  const [useProductCode, setUseProductCode] = useState(false)

  const [showAddressDropdown, setShowAddressDropdown] = useState(false)
  const [showActivityDropdown, setShowActivityDropdown] = useState(false)
  const [ufValue, setUfValue] = useState(null)
  const [loadingUF, setLoadingUF] = useState(false)

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (!event.target.closest('.address-selector')) {
        setShowAddressDropdown(false)
      }
      if (!event.target.closest('.activity-selector')) {
        setShowActivityDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Fetch UF when currency is UF
  useEffect(() => {
    const fetchUF = async () => {
      setLoadingUF(true)
      try {
        const response = await fetch('https://mindicador.cl/api/uf')
        const data = await response.json()
        if (data?.serie?.[0]?.valor) {
          const val = data.serie[0].valor
          setUfValue(val)

          // Auto-update prices if currency is already UF
          if (formState.currency === 'UF') {
            setFormState(prev => ({
              ...prev,
              details: prev.details.map(d => ({ ...d, unit_price: val }))
            }))
          }
        }
      } catch (err) {
        console.error('Error fetching UF', err)
      } finally {
        setLoadingUF(false)
      }
    }

    if (formState.currency === 'UF' && !ufValue) {
      fetchUF()
    }
  }, [formState.currency, setFormState, ufValue])

  // If currency changes to UF, and we have ufValue, update prices
  useEffect(() => {
    if (formState.currency === 'UF' && ufValue) {
      setFormState(prev => ({
        ...prev,
        details: prev.details.map(d => ({ ...d, unit_price: ufValue }))
      }))
    }
  }, [formState.currency, ufValue, setFormState])

  const handleSearchClient = async (explicitRut = null) => {
    const rutToSearch = explicitRut || rutSearch
    if (!rutToSearch) return

    setLoadingClient(true)
    try {
      const api = new FacturaAPI()
      const { data } = await api.getSearchEntityRut(rutToSearch)

      if (data) {
        const address = data.addresses?.[0] || {}
        const activity = data.activities?.[0] || {}
        setMasterEntityId(data.id)
        setFormState(prev => ({
          ...prev,
          client: {
            id: data.id,
            tax_id: data.tax_id || rutToSearch,
            rut: data.tax_id || rutToSearch,
            razon_social: data.name || '',
            direccion: address.address || '',
            ciudad: address.district?.city?.name || '',
            comuna: address.district?.name || '',
            giro: activity.name || '',
            codigo_actividad: activity.code || '',
            contacto: '',
            addresses: data.addresses || [],
            activities: data.activities || [],
            isAddressManual: false,
            isActivityManual: false
          }
        }))
        clearError('client')
      }
    } catch (error) {
      console.error('Error searching client', error)
    } finally {
      setLoadingClient(false)
    }
  }

  // Hydrate client details on mount if we have a RUT but no activities/addresses
  useEffect(() => {
    if (
      formState.client?.rut &&
      (!formState.client.addresses?.length || !formState.client.activities?.length)
    ) {
      handleSearchClient(formState.client.rut)
    }
  }, [])

  const handleSelectAddress = addr => {
    setFormState(prev => ({
      ...prev,
      client: {
        ...prev.client,
        direccion: addr.address,
        ciudad: addr.district?.city?.name || '',
        comuna: addr.district?.name || '',
        isAddressManual: false
      }
    }))
    setShowAddressDropdown(false)
  }

  const handleSelectActivity = act => {
    setFormState(prev => ({
      ...prev,
      client: {
        ...prev.client,
        giro: act.name,
        codigo_actividad: act.code,
        isActivityManual: false
      }
    }))
    setShowActivityDropdown(false)
  }

  const toggleAddressManual = () => {
    setFormState(prev => ({
      ...prev,
      client: {
        ...prev.client,
        isAddressManual: true,
        direccion: '',
        ciudad: '',
        comuna: ''
      }
    }))
    setShowAddressDropdown(false)
  }

  const toggleActivityManual = () => {
    setFormState(prev => ({
      ...prev,
      client: {
        ...prev.client,
        isActivityManual: true,
        giro: '',
        codigo_actividad: ''
      }
    }))
    setShowActivityDropdown(false)
  }

  const addDetail = () => {
    setFormState(prev => ({
      ...prev,
      details: [
        ...prev.details,
        {
          id: Date.now(),
          item_type_code: '',
          item_code: '',
          item_name: '',
          showDescription: false,
          quantity: 1,
          unit_price: prev.currency === 'UF' ? ufValue || 0 : 0,
          item_description: ''
        }
      ]
    }))
  }

  const removeDetail = id => {
    setFormState(prev => ({
      ...prev,
      details: prev.details.filter(d => d.id !== id)
    }))
  }

  const updateDetail = (id, field, value) => {
    setFormState(prev => {
      const newDetails = prev.details.map(d => {
        if (d.id === id) {
          return { ...d, [field]: value }
        }
        return d
      })
      return { ...prev, details: newDetails }
    })
  }

  const addReference = () => {
    const currentRefs = formState.references || []
    if (currentRefs.length >= 3) {
      sileo.warning({
        title: 'Límite de Referencias',
        description: 'Solo puedes agregar hasta 3 referencias 🤯'
      })
      return
    }
    setFormState(prev => ({
      ...prev,
      references: [
        ...currentRefs,
        {
          id: Date.now() + Math.random(),
          reference_type: '',
          reference_folio: '',
          reference_date: '',
          reference_reason: '',
          dte_type_code: ''
        }
      ]
    }))
  }

  const removeReference = id => {
    setFormState(prev => ({
      ...prev,
      references: (prev.references || []).filter(r => r.id !== id)
    }))
  }

  const updateReference = (id, field, value) => {
    setFormState(prev => {
      const newRefs = (prev.references || []).map(r => {
        if (r.id === id) return { ...r, [field]: value }
        return r
      })
      return { ...prev, references: newRefs }
    })
  }

  const handleChange = (field, value) => {
    setFormState(prev => ({ ...prev, [field]: value }))
  }

  const clientFound = formState.client && (formState.client.rut || formState.client.razon_social)

  const formatMoney = value => value.toLocaleString('es-CL')

  return (
    <div className='bg-white p-6 rounded-lg shadow-sm border border-gray-100'>
      <div className='section-title'>
        <FileText size={20} className='text-primary' />
        <h3>Información del Documento</h3>
      </div>

      <div className='space-y-6'>
        {/* Client Search */}
        <div>
          <div className='flex justify-between items-end mb-1'>
            <label className='block text-sm font-medium text-gray-700'>Cliente</label>
            {(clientFound || rutSearch) && (
              <button
                onClick={() => {
                  setRutSearch('')
                  setFormState(prev => ({
                    ...prev,
                    client: {
                      rut: '',
                      razon_social: '',
                      direccion: '',
                      ciudad: '',
                      comuna: '',
                      giro: '',
                      codigo_actividad: '',
                      contacto: '',
                      addresses: [],
                      activities: [],
                      isAddressManual: false,
                      isActivityManual: false
                    }
                  }))
                  clearError('client')
                }}
                className='text-red-500 text-xs hover:text-red-700 flex items-center gap-1 transition-colors'
              >
                <span>×</span> Limpiar
              </button>
            )}
          </div>

          <input
            value={rutSearch}
            onChange={e => setRutSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearchClient()}
            onBlur={handleSearchClient}
            placeholder='Buscar cliente por RUT o nombre...'
            className={`input w-full ${errors?.client ? 'border-red-300 bg-red-50' : 'border-gray-200'
              }`}
          />
          {errors?.client && <p className='text-red-500 text-xs mt-1'>{errors.client}</p>}

          {loadingClient && (
            <div className='flex items-center gap-2 mt-2 text-xs text-primary animate-pulse'>
              <div className='animate-spin h-3 w-3 border-2 border-primary border-t-transparent rounded-full'></div>
              <span>Cargando direcciones y actividades...</span>
            </div>
          )}
        </div>

        {/* Extended Client Info (Only if found) */}
        {clientFound && (
          <div className='flex flex-col gap-6 animate-in fade-in slide-in-from-top-2 duration-300'>
            {/* Razon Social */}
            <div>
              <label className='block text-xs font-medium text-gray-500 mb-1'>Razón Social</label>
              <input
                value={formState.client.razon_social}
                onChange={e =>
                  setFormState(prev => ({
                    ...prev,
                    client: { ...prev.client, razon_social: e.target.value }
                  }))
                }
                className='w-full border border-gray-200 rounded px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20'
              />
              <p className='text-[10px] text-gray-400 mt-1'>
                Esta será la razón social que aparecerá en la factura
              </p>
            </div>

            {/* Grid for Address and Activity */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              {/* Dirección */}
              <div className='address-selector relative'>
                <div className='flex justify-between items-center mb-1'>
                  <label className='block text-xs font-medium text-gray-500'>Dirección</label>
                  {formState.client.isAddressManual && (
                    <button
                      onClick={() =>
                        setFormState(prev => ({
                          ...prev,
                          client: { ...prev.client, isAddressManual: false }
                        }))
                      }
                      className='text-[10px] text-blue-500 hover:underline'
                    >
                      Ver lista
                    </button>
                  )}
                </div>

                {!formState.client.isAddressManual ? (
                  <div
                    className='w-full bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-700 cursor-pointer relative hover:border-gray-300 transition-colors'
                    onClick={() => setShowAddressDropdown(!showAddressDropdown)}
                  >
                    <div className='font-medium truncate pr-6'>
                      {formState.client.direccion || 'Seleccionar dirección'}
                    </div>
                    {(formState.client.ciudad || formState.client.comuna) && (
                      <div className='text-gray-500 text-xs mt-0.5 truncate'>
                        {[formState.client.ciudad, formState.client.comuna]
                          .filter(Boolean)
                          .join(', ')}
                      </div>
                    )}

                    <div className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400'>
                      <ChevronDown size={16} />
                    </div>

                    {showAddressDropdown && (
                      <div className='absolute left-0 top-full mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg z-50 max-h-60 overflow-auto'>
                        <div
                          className='p-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer'
                          onClick={e => {
                            e.stopPropagation()
                            toggleAddressManual()
                          }}
                        >
                          <div className='text-blue-500 font-medium text-xs mb-0.5'>
                            Ingresar manualmente
                          </div>
                        </div>

                        {(formState.client.addresses || []).map((addr, idx) => (
                          <div
                            key={idx}
                            className='p-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer'
                            onClick={e => {
                              e.stopPropagation()
                              handleSelectAddress(addr)
                            }}
                          >
                            <div className='font-medium text-xs mb-0.5'>{addr.address}</div>
                            <div className='text-gray-500 text-[10px]'>
                              {[addr.district?.name, addr.district?.city?.name]
                                .filter(Boolean)
                                .join(', ')}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className='space-y-2'>
                    <input
                      placeholder='Dirección'
                      value={formState.client.direccion}
                      onChange={e =>
                        setFormState(prev => ({
                          ...prev,
                          client: { ...prev.client, direccion: e.target.value }
                        }))
                      }
                      className='w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20'
                    />
                    <div className='flex gap-2'>
                      <input
                        placeholder='Comuna'
                        value={formState.client.comuna}
                        onChange={e =>
                          setFormState(prev => ({
                            ...prev,
                            client: { ...prev.client, comuna: e.target.value }
                          }))
                        }
                        className='w-1/2 border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20'
                      />
                      <input
                        placeholder='Ciudad'
                        value={formState.client.ciudad}
                        onChange={e =>
                          setFormState(prev => ({
                            ...prev,
                            client: { ...prev.client, ciudad: e.target.value }
                          }))
                        }
                        className='w-1/2 border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20'
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Actividad Económica */}
              <div className='activity-selector relative'>
                <div className='flex justify-between items-center mb-1'>
                  <label className='block text-xs font-medium text-gray-500'>
                    Actividad Económica
                  </label>
                  {formState.client.isActivityManual && (
                    <button
                      onClick={() =>
                        setFormState(prev => ({
                          ...prev,
                          client: { ...prev.client, isActivityManual: false }
                        }))
                      }
                      className='text-[10px] text-blue-500 hover:underline'
                    >
                      Ver lista
                    </button>
                  )}
                </div>

                {!formState.client.isActivityManual ? (
                  <div
                    className='w-full bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-700 cursor-pointer relative hover:border-gray-300 transition-colors'
                    onClick={() => setShowActivityDropdown(!showActivityDropdown)}
                  >
                    <div className='font-medium pr-6 truncate'>
                      {formState.client.giro || 'Seleccionar actividad'}
                    </div>
                    {formState.client.codigo_actividad && (
                      <div className='text-gray-500 text-xs mt-0.5'>
                        Código: {formState.client.codigo_actividad}
                      </div>
                    )}

                    <div className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400'>
                      <ChevronDown size={16} />
                    </div>

                    {showActivityDropdown && (
                      <div className='absolute left-0 top-full mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg z-50 max-h-60 overflow-auto'>
                        <div
                          className='p-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer'
                          onClick={e => {
                            e.stopPropagation()
                            toggleActivityManual()
                          }}
                        >
                          <div className='text-blue-500 font-medium text-xs mb-0.5'>
                            Ingresar manualmente
                          </div>
                        </div>

                        {(formState.client.activities || []).map((act, idx) => (
                          <div
                            key={idx}
                            className='p-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer'
                            onClick={e => {
                              e.stopPropagation()
                              handleSelectActivity(act)
                            }}
                          >
                            <div className='font-medium text-xs mb-0.5'>{act.name}</div>
                            <div className='text-gray-500 text-[10px]'>Código: {act.code}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className='space-y-2'>
                    <input
                      placeholder='Nombre de la actividad económica'
                      value={formState.client.giro}
                      onChange={e =>
                        setFormState(prev => ({
                          ...prev,
                          client: { ...prev.client, giro: e.target.value }
                        }))
                      }
                      className='w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20'
                    />
                    <input
                      placeholder='Código de actividad'
                      value={formState.client.codigo_actividad}
                      onChange={e =>
                        setFormState(prev => ({
                          ...prev,
                          client: { ...prev.client, codigo_actividad: e.target.value }
                        }))
                      }
                      className='w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20'
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Grid for Contact, Type and Currency */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <div>
                <label className='block text-xs font-medium text-gray-500 mb-1'>
                  Contacto (opcional)
                </label>
                <input
                  type='text'
                  placeholder='Nombre del contacto'
                  value={formState.client.contacto || ''}
                  onChange={e =>
                    setFormState(prev => ({
                      ...prev,
                      client: { ...prev.client, contacto: e.target.value }
                    }))
                  }
                  className='w-full border border-gray-200 rounded px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20'
                />
              </div>

              <div>
                <label className='block text-xs font-medium text-gray-500 mb-1'>
                  Tipo de Documento
                </label>
                <select
                  value={formState.documentType}
                  onChange={e => handleChange('documentType', e.target.value)}
                  className='input w-full p-2'
                >
                  <option value='33'>Factura Electrónica (33)</option>
                  <option value='34'>Factura Electrónica Exenta (34)</option>
                  <option value='61'>Nota de Crédito Electrónica (61)</option>
                  <option value='56'>Nota de Débito Electrónica (56)</option>
                </select>
              </div>

              <div className={formState.currency === 'UF' ? 'col-span-1' : 'col-span-1 md:col-span-2'}>
                <label className='block text-xs font-medium text-gray-500 mb-1'>Moneda</label>
                <input
                  value={formState.currency}
                  readOnly
                  className='border border-gray-200 rounded px-3 py-2 text-sm text-gray-700 focus:outline-none cursor-default bg-gray-50'
                />
              </div>

              {formState.currency === 'UF' && (
                <div>
                  <label className='block text-xs font-medium text-gray-500 mb-1'>
                    Día para Tipo de Cambio (UF)
                  </label>
                  <input
                    type='number'
                    min='1'
                    max='31'
                    value={formState.config.currency_day || 1}
                    onChange={e =>
                      setFormState(prev => ({
                        ...prev,
                        config: { ...prev.config, currency_day: Number(e.target.value) }
                      }))
                    }
                    className='input w-full p-2 border-emerald-500'
                  />
                </div>
              )}
            </div>

            {formState.currency === 'UF' && (
              <div className='space-y-2 animate-in fade-in slide-in-from-top-1'>
                <p className='text-xs text-gray-500'>
                  Se utilizará el valor de UF del día{' '}
                  <span className='font-bold text-gray-700'>{formState.config.currency_day}</span> de
                  cada mes para calcular el monto en pesos chilenos.
                </p>
                <div className='flex gap-2 p-3 bg-blue-50 border border-blue-100 rounded-lg'>
                  <div className='w-5 h-5 flex items-center justify-center bg-blue-500 text-white rounded text-[10px] font-bold shrink-0'>
                    i
                  </div>
                  <p className='text-[11px] text-blue-700 leading-tight'>
                    Para documentos en UF, ingresa solo las cantidades. El precio se calculará
                    automáticamente usando el valor de UF del día especificado.{' '}
                    {ufValue && (
                      <span className='font-bold'>
                        (Valor actual UF: ${formatMoney(Math.round(ufValue))})
                      </span>
                    )}
                    {loadingUF && <span className='animate-pulse'> (Buscando valor UF...)</span>}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* PRODUCTOS */}
        <section>
          <div className='section-title mt-10'>
            <FileText size={20} className='text-primary' />
            <h3>Detalles del Documento</h3>
          </div>

          <div className='flex items-center justify-between'>
            <div>
              <div className='flex items-center gap-2'>
                <h2 className='font-semibold text-sm md:text-base'>Cod Producto</h2>
                <input
                  type='checkbox'
                  checked={useProductCode}
                  onChange={e => setUseProductCode(e.target.checked)}
                />
              </div>

              <p className='text-xs text-gray-500'>
                Nombre del producto, cantidad y precio son obligatorios.
              </p>
            </div>
          </div>

          {/* Tabla de productos */}
          <div className='overflow-auto bg-white'>
            <table className='w-full text-xs md:text-sm detalle-table'>
              <thead className='h-10 bg-gray-100'>
                <tr>
                  {useProductCode && <th className='w-28 text-left pl-2'>Tipo código</th>}
                  {useProductCode && <th className='w-24 text-left pl-2'>Código</th>}

                  <th className='w-[220px] text-left pl-2'>
                    Nombre Producto <span className='text-red-500'>*</span>
                  </th>

                  <th className='w-16 text-center'>Descrip.</th>

                  <th className='w-20 text-center'>
                    Cantidad <span className='text-red-500'>*</span>
                  </th>

                  <th className='w-28 text-right pr-2'>
                    Precio <span className='text-red-500'>*</span>
                  </th>

                  <th className='w-24 text-right pr-2'>SubTotal</th>

                  <th className='w-10'></th>
                </tr>
              </thead>

              <tbody>
                {formState.details.map((d, index) => {
                  const qty = Number(d.quantity) || 0
                  const price = Number(d.unit_price) || 0

                  const lineGross = qty * price
                  const lineNet = lineGross

                  return (
                    <React.Fragment key={d.id}>
                      {/* Fila principal */}
                      <tr>
                        {useProductCode && (
                          <td className='p-1'>
                            <input
                              className='input w-full p-1'
                              value={d.item_type_code || ''}
                              onChange={e => updateDetail(d.id, 'item_type_code', e.target.value)}
                              placeholder='Ej: CPPU'
                            />
                          </td>
                        )}

                        {useProductCode && (
                          <td className='p-1'>
                            <input
                              className='input w-full p-1'
                              value={d.item_code || ''}
                              onChange={e => updateDetail(d.id, 'item_code', e.target.value)}
                              placeholder='1500'
                            />
                          </td>
                        )}

                        {/* Nombre producto */}
                        <td className='p-1'>
                          <input
                            className='input w-full p-1'
                            value={d.item_name || ''}
                            onChange={e => updateDetail(d.id, 'item_name', e.target.value)}
                            required
                            placeholder='Nombre del producto'
                          />
                        </td>

                        {/* Checkbox descripción */}
                        <td className='text-center align-middle p-1'>
                          <input
                            type='checkbox'
                            checked={d.showDescription || false}
                            onChange={e => updateDetail(d.id, 'showDescription', e.target.checked)}
                          />
                        </td>

                        {/* Cantidad */}
                        <td className='p-1'>
                          <input
                            type='number'
                            className='input w-full p-1 text-right'
                            value={d.quantity}
                            onChange={e => updateDetail(d.id, 'quantity', e.target.value)}
                            required
                            min='1'
                          />
                        </td>

                        {/* Precio */}
                        <td className='p-1'>
                          <input
                            type='number'
                            className={`input w-full p-1 text-right ${formState.currency === 'UF' ? 'bg-gray-50 text-gray-500' : ''
                              }`}
                            value={d.unit_price}
                            onChange={e => updateDetail(d.id, 'unit_price', e.target.value)}
                            required
                            min='0'
                            readOnly={formState.currency === 'UF'}
                          />
                        </td>

                        {/* Subtotal */}
                        <td className='text-right border border-[#e5e7eb] rounded-[0.4rem] pr-2 p-1'>
                          ${formatMoney(Math.round(lineNet))}
                        </td>

                        {/* Basurero */}
                        <td className='text-center p-1'>
                          <button
                            type='button'
                            className='trash-btn'
                            onClick={() => removeDetail(d.id)}
                            aria-label='Eliminar ítem'
                          >
                            <DeleteOutlineOutlinedIcon fontSize='small' />
                          </button>
                        </td>
                      </tr>

                      {/* Fila de descripción extendida */}
                      {d.showDescription && (
                        <tr className='descripcion-row'>
                          {useProductCode && <td></td>}
                          {useProductCode && <td></td>}
                          <td colSpan={5} className='p-1'>
                            <textarea
                              className='input w-full p-2 descripcion-textarea'
                              value={d.item_description || ''}
                              onChange={e => updateDetail(d.id, 'item_description', e.target.value)}
                              placeholder='Ingrese sólo si requiere ingresar una descripción extendida del producto o servicio'
                              rows={2}
                            />
                          </td>
                          <td></td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>

            {/* Agrega línea de Detalle (Botón naranja grande) */}
            <div className='flex justify-center items-center mt-3 bg-gray-100 h-14'>
              <button type='button' onClick={addDetail} className='cursor-pointer inline-flex items-center justify-center gap-2 rounded-full bg-accent2 px-6 py-2.5 text-xs font-bold text-white shadow-lg shadow-black/20 transition-all hover:bg-accent2/90 active:scale-95'>
                Agregar línea de detalle
              </button>
            </div>
          </div>
        </section>

        {/* REFERENCIAS - NEW STYLE MATCHING CreateFactura.js */}
        <section className='bg-gray-100 p-4 rounded-md shadow border border-gray-200 mt-8 space-y-4'>
          {/* Cabecera: checkbox + Referencias text */}
          <div className='flex items-center gap-2 text-sm font-semibold text-gray-800'>
            <input
              type='checkbox'
              checked={formState.showReferencesBox || false}
              onChange={e => {
                const checked = e.target.checked
                setFormState(prev => ({
                  ...prev,
                  showReferencesBox: checked,
                  references:
                    checked && (prev.references || []).length === 0
                      ? [
                        {
                          id: Date.now(),
                          dte_type_code: '',
                          reference_folio: '',
                          reference_date: '',
                          reference_reason: ''
                        }
                      ]
                      : prev.references
                }))
              }}
            />
            <span>Referencias</span>
          </div>

          {formState.showReferencesBox && (
            <div className='overflow-auto animate-in fade-in duration-300'>
              {/* Tabla de referencias estilo SII / CreateFactura.js */}
              <table className='w-full text-xs md:text-sm refs-table'>
                <thead className='bg-gray-100 border-b border-gray-300'>
                  <tr>
                    <th className='text-left px-2 py-2 font-semibold text-gray-700'>
                      Tipo de documento
                    </th>
                    <th className='text-left px-2 py-2 font-semibold text-gray-700 w-28'>
                      Folio Ref.
                    </th>
                    <th className='text-left px-2 py-2 font-semibold text-gray-700 w-40'>
                      Fecha Ref.
                    </th>
                    <th className='text-left px-2 py-2 font-semibold text-gray-700'>
                      Razón Referencia
                    </th>
                    <th className='w-10'></th>
                  </tr>
                </thead>

                <tbody className='bg-white'>
                  {(formState.references || []).map((r, idx) => (
                    <tr key={r.id || idx}>
                      {/* Tipo de documento */}
                      <td className='p-1'>
                        <select
                          className='input text-xs'
                          value={r.dte_type_code || ''}
                          onChange={e => updateReference(r.id, 'dte_type_code', e.target.value)}
                        >
                          <option value=''>Seleccionar tipo…</option>
                          <option value='33'>Factura Electrónica (33)</option>
                          <option value='34'>Factura Exenta (34)</option>
                          <option value='61'>Nota de Crédito (61)</option>
                          <option value='56'>Nota de Débito (56)</option>
                          <option value='801'>Orden de Compra (801)</option>
                          <option value='H'>Hoja de Entrada de Servicios (H)</option>
                        </select>
                      </td>

                      {/* Folio ref */}
                      <td className='p-1'>
                        <input
                          className='input text-xs'
                          placeholder=''
                          type='text'
                          value={r.reference_folio || ''}
                          onChange={e => updateReference(r.id, 'reference_folio', e.target.value)}
                        />
                      </td>

                      {/* Fecha ref */}
                      <td className='p-1'>
                        <input
                          className='input text-xs'
                          type='date'
                          value={r.reference_date || ''}
                          onChange={e => updateReference(r.id, 'reference_date', e.target.value)}
                        />
                      </td>

                      {/* Razón referencia */}
                      <td className='p-1'>
                        <input
                          className='input text-xs'
                          placeholder=''
                          value={r.reference_reason || ''}
                          onChange={e => updateReference(r.id, 'reference_reason', e.target.value)}
                        />
                      </td>

                      {/* Basurero */}
                      <td className='text-center p-1'>
                        <button
                          type='button'
                          className='trash-btn'
                          onClick={() => removeReference(r.id)}
                          aria-label='Eliminar referencia'
                        >
                          <DeleteOutlineOutlinedIcon fontSize='small' />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Botón para agregar más referencias, centrado abajo - Deshabilitado si ya hay 3 */}
              <div className='flex justify-center mt-3 h-12 items-center bg-transparent'>
                <button
                  type='button'
                  className='cursor-pointer inline-flex items-center justify-center gap-2 rounded-full bg-accent2 px-6 py-2.5 text-xs font-bold text-white shadow-lg shadow-black/20 transition-all hover:bg-accent2/90 active:scale-95'
                  onClick={addReference}
                  disabled={(formState.references || []).length >= 3}
                >
                  Agregar referencia
                </button>
              </div>
            </div>
          )}
        </section>

        <FacturaTotales details={formState.details} dteCode={formState.documentType} />
      </div>

      <style>{`
          .input {
            width: 100%;
            padding: 0.35rem 0.6rem;
            border: 1px solid #e5e7eb;
            border-radius: 0.4rem;
            outline: none;
            font-size: 0.8rem;
          }

          .input:focus {
            box-shadow: 0 0 0 2px rgba(59,130,246,.3);
            border-color: #93c5fd;
          }

          .detalle-table {
            border-collapse: separate;
            border-spacing: 0 6px;  
          }

          .detalle-table thead th {
             color: #4b5563;
             font-weight: 700;
             font-size: 0.75rem;
             border-bottom: 2px solid #e5e7eb;
          }

          .refs-table {
            border-collapse: separate;
            border-spacing: 0 4px;  
            width: 100%;
          }

          .trash-btn {
            color: #ef4444; 
            cursor: pointer;
          }
          .trash-btn:hover {
            color: #b91c1c;     
          }
          .descripcion-textarea {
            font-size: 0.75rem;
            resize: none;
            background: #fdfdfd;
          }
        `}</style>
    </div>
  )
}
